import {ChangeDetectorRef, Component, inject, OnDestroy, OnInit} from "@angular/core";
import {LocationStrategy} from "@angular/common";
import {TranslateService} from "@ngx-translate/core";
import {GlobalModelService} from "../services/GlobalModel.service";
import {NGXLogger} from "ngx-logger";
import {AppService} from "../services/app.service";
import {Subscription} from "rxjs";
import {custom} from "devextreme/ui/dialog";
import {ToastService} from "../services/toast.service";
import {EventBusNotificationService} from "../services/notification/interfaces/EventBusService";
import {NotificationService} from "../services/notification/notification.service";
import {DxFormComponent} from "devextreme-angular";
import {SpinnerService} from "../services/spinner.service";
import {UserMetricInstance} from "../models/user/Metric/UserMetricInstance";
import {DataTypeGroupEnum} from "../enums/dashboard/DataTypeUnitEnum";

@Component({
  selector: '',
  template: 'NO-UI'
})
export abstract class BaseComponent implements OnInit, OnDestroy {

  protected spinnerService = inject(SpinnerService);
  gmService: GlobalModelService = inject(GlobalModelService);
  appService: AppService = inject(AppService);
  cdr: ChangeDetectorRef = inject(ChangeDetectorRef);
  protected location = inject(LocationStrategy);
  protected translate = inject(TranslateService);
  protected eventBus: EventBusNotificationService = inject(NotificationService);
  protected toastService: ToastService = inject(ToastService);
  protected subscriptions: Subscription[];
  protected logger: NGXLogger = inject(NGXLogger);
  protected isMobile?: boolean;
  protected isSmallScreen?: boolean;
  protected isMediumScreen?: boolean;
  protected primaryButtonAttributes = {
    class: 'primaryButton'
  };

  constructor() {
    this.subscriptions = [];
    window.onresize = () => {
      this.isMediumScreen = this.appService.isMediumScreen();
      this.isSmallScreen = this.appService.isSmallScreen();
      this.isMobile = this.appService.isMobileScreen();
    };
  }

  ngOnInit(): void {

    // Set mobile flag
    this.isMobile = this.appService.isMobileScreen();
    this.isSmallScreen = this.appService.isSmallScreen();
    this.isMediumScreen = this.appService.isMediumScreen();

  }

  ngOnDestroy() {
    for (const subscription of this.subscriptions) {
      subscription.unsubscribe();
    }
    window.onresize = null;
  }

  showDiscardEditsDialog() {
    return custom({
      title: this.translate.instant("REGISTRY.BASE.DISCARD_CHANGES"),
      messageHtml: this.translate.instant("REGISTRY.BASE.CONFIRM_DISCARD_CHANGES"),
      buttons: [
        {
          text: this.translate.instant("REGISTRY.BASE.YES_DISCARD"),
          icon: 'revert',
          stylingMode: 'contained',
          type: 'default',
          onClick: function () {
            return true
          }
        },
        {
          text: this.translate.instant("REGISTRY.BASE.NO_DISCARD"),
          icon: 'edit',
          type: 'default',
          onClick: function () {
            return false
          }
        }
      ]
    });
  }

  showDiscardEditsDialogWithPromise() {
    return new Promise((resolve) => {
      custom({
        title: this.translate.instant("REGISTRY.BASE.DISCARD_CHANGES"),
        messageHtml: this.translate.instant("REGISTRY.BASE.CONFIRM_DISCARD_CHANGES"),
        buttons: [
          {
            text: this.translate.instant("REGISTRY.BASE.YES_DISCARD"),
            icon: 'revert',
            stylingMode: 'contained',
            type: 'default',
            onClick: function () {
              resolve(true);
            }
          },
          {
            text: this.translate.instant("REGISTRY.BASE.NO_DISCARD"),
            icon: 'edit',
            type: 'default',
            onClick: function () {
              resolve(false);
            }
          }
        ]
      }).show();
    });
  }

  /**
   * Warns that some row is still "in modifica" (unconfirmed) before a save proceeds - those
   * edits would otherwise be silently dropped since they never made it into the bound data.
   */
  showSaveWithPendingEditDialogWithPromise() {
    return new Promise((resolve) => {
      custom({
        title: this.translate.instant("REGISTRY.BASE.PENDING_EDIT_TITLE"),
        messageHtml: this.translate.instant("REGISTRY.BASE.PENDING_EDIT_MESSAGE"),
        buttons: [
          {
            text: this.translate.instant("REGISTRY.BASE.PENDING_EDIT_CONTINUE"),
            icon: 'save',
            stylingMode: 'contained',
            type: 'default',
            onClick: function () {
              resolve(true);
            }
          },
          {
            text: this.translate.instant("REGISTRY.BASE.PENDING_EDIT_CANCEL"),
            icon: 'edit',
            type: 'default',
            onClick: function () {
              resolve(false);
            }
          }
        ]
      }).show();
    });
  }

  showDeleteDialog(name: string) {
    return custom({
      title: this.translate.instant('COMMON.REMOVE'),
      messageHtml: "<i>" + this.translate.instant('COMMON.ARE-U-SURE') + " " + name + "?</i>",
      buttons: [
        {
          text: this.translate.instant("BUTTONS.DELETE"),
          icon: 'trash',
          stylingMode: 'contained',
          type: 'danger',
          onClick: function () {
            return true
          }
        },
        {
          text: this.translate.instant("BUTTONS.CANCEL"), icon: 'revert', type: 'default', onClick: function () {
            return false
          }
        }
      ]
    })
  }

  showExecDialog(name: string, email: string) {
    return custom({
      title: this.translate.instant('COMMON.EXEC'),
      messageHtml: "<i>" + this.translate.instant('COMMON.ARE-U-SURE-EXEC') + " \"" + name + "\" " + this.translate.instant('COMMON.EXEC-TO') + " \"" + email + "\"?</i>",
      buttons: [
        {
          text: this.translate.instant("BUTTONS.EXEC"),
          icon: 'email',
          stylingMode: 'contained',
          type: 'default',
          onClick: function () {
            return true
          }
        },
        {
          text: this.translate.instant("BUTTONS.CANCEL"), icon: 'revert', type: 'default', onClick: function () {
            return false
          }
        }
      ]
    })
  }

  checkTabValidity(title: any, form: DxFormComponent) {
    if (title.items) {
      for (let item of title.items) {
        if (!!item.validationRules && item.visible !== false) {
          let editor = form.instance.getEditor(item.dataField);
          if (!!editor) {
            if (!editor.option().isValid)
              return false
          }
        }
      }
    }
    return true;
  }

  /**
   * Finds and focuses the first invalid field inside an invalid tabbed form
   *
   * @param formInstance the form that we are checking
   */
  focusInvalidTab(formInstance: DxFormComponent) {
    if (!!formInstance && !!formInstance.instance.option('items') && formInstance.instance.option('items')!.length > 0) {
      const tabPanel = (formInstance.instance.option('items')![0] as any);
      const tabs = tabPanel.tabs;
      for (let tabIndex = 0; tabIndex < tabs.length; tabIndex++) {
        const tab = tabs[tabIndex];
        for (const item of tab.items) {
          if (!!formInstance.instance.getEditor(item.dataField)) {
            const fieldValidation = formInstance.instance.getEditor(item.dataField)!.option().isValid;
            if (!fieldValidation) {
              tabPanel.tabPanelOptions.selectedIndex = tabIndex;
              formInstance.instance.repaint();
              this.cdr.detectChanges();
              formInstance.instance.getEditor(item.dataField)!.focus();
              formInstance.instance.validate();
              return;
            }
          }
        }
      }
    }
  }

  /**
   * Moves an item in array arr from old_index to new_index
   * @param arr
   * @param old_index
   * @param new_index
   */
  array_move(arr: any[], old_index: number, new_index: number) {
    if (new_index >= arr.length) {
      var k = new_index - arr.length + 1;
      while (k--) {
        arr.push(undefined);
      }
    }
    arr.splice(new_index, 0, arr.splice(old_index, 1)[0]);
    return arr; // for testing
  };

  protected getStylizedCSSVariable = function (variable: string) {
    return getComputedStyle(document.documentElement).getPropertyValue(variable);
  };

  protected getType(x: any) {
    return typeof x;
  }

  getProtocolTypeValue(protocolTypeName: string): string {
    return this.gmService.protocolTypeValues.find(protocolType => protocolType.value === protocolTypeName).value;
  }

  metricIsNumeric(metric: UserMetricInstance) {
    // Check the dataTypeGroup only Integer and Float are the DataTypes allowed as Numeric
    return metric.dataTypeGroup === DataTypeGroupEnum.INTEGER || metric.dataTypeGroup === DataTypeGroupEnum.FLOAT;
  }

  numericSensitiveSort(a: any, b: any): number {
    return String(a ?? '').localeCompare(String(b ?? ''), undefined, {numeric: true, sensitivity: 'base'});
  }
}
